import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";

// Security: Input validation schema
const reviewSchema = z.object({
  rating: z.number().int().min(1, "Vælg mindst 1 stjerne").max(5, "Maksimalt 5 stjerner"),
  text: z.string().max(1000, "Kommentar må ikke overstige 1000 tegn").optional(),
});

interface SubmitReviewProps {
  bookingId: string;
  listingTitle: string;
  reviewType: 'guest_to_host' | 'host_to_guest';
  targetUserName?: string;
  onSubmitted?: () => void;
}

export default function SubmitReview({ bookingId, listingTitle, reviewType, targetUserName, onSubmitted }: SubmitReviewProps) {
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hoveredRating, setHoveredRating] = useState(0);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Security: Validate input before sending to database
      const validated = reviewSchema.parse({
        rating,
        text: text.trim() || undefined,
      });

      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("Du skal være logget ind for at anmelde");
        return;
      }

      const { data: newReview, error } = await supabase
        .from("reviews")
        .insert({
          booking_id: bookingId,
          reviewer_id: user.id,
          rating: validated.rating,
          text: validated.text || null,
          review_type: reviewType,
        })
        .select()
        .single();

      if (error) {
        // Check if it's a duplicate review error
        if (error.code === '23505') {
          toast.error("Du har allerede bedømt denne booking");
        } else {
          throw error;
        }
        return;
      }

      // Send notification email (fire and forget)
      if (newReview) {
        try {
          await supabase.functions.invoke("send-review-notification", {
            body: { review_id: newReview.id }
          });
        } catch (notifyErr) {
          console.error("Failed to send review notification:", notifyErr);
          // Don't show error to user - notification is optional
        }
      }

      toast.success("Tak for din anmeldelse!");
      setRating(0);
      setText("");
      onSubmitted?.();
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
      } else {
        console.error("Error submitting review:", error);
        toast.error(error?.message || "Der skete en fejl. Prøv igen.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          {reviewType === 'guest_to_host' ? 'Bedøm din oplevelse' : 'Bedøm lejer'}
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {reviewType === 'guest_to_host' ? listingTitle : `Lejer: ${targetUserName || 'Bruger'}`}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="rating-input" className="text-sm font-medium">Bedømmelse</label>
          <div className="flex items-center space-x-1" role="radiogroup" aria-labelledby="rating-input">
            {Array.from({ length: 5 }).map((_, i) => (
              <button
                key={i}
                type="button"
                role="radio"
                aria-checked={rating === i + 1}
                aria-label={`${i + 1} stjerner`}
                className="p-1"
                onMouseEnter={() => setHoveredRating(i + 1)}
                onMouseLeave={() => setHoveredRating(0)}
                onClick={() => setRating(i + 1)}
              >
                <Star
                  className={`h-6 w-6 transition-colors ${
                    i < (hoveredRating || rating)
                      ? "fill-warning text-warning"
                      : "text-muted-foreground hover:text-warning"
                  }`}
                />
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="review-comment" className="text-sm font-medium">Kommentar (valgfri)</label>
          <Textarea
            id="review-comment"
            placeholder={reviewType === 'guest_to_host' ? "Fortæl om din oplevelse..." : "Fortæl om lejeren..."}
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={1000}
            rows={3}
          />
          <p className="text-xs text-muted-foreground">
            {text.length}/1000 tegn
          </p>
        </div>

        <Button 
          onClick={handleSubmit}
          disabled={rating === 0 || isSubmitting}
          className="w-full"
        >
          {isSubmitting ? "Sender..." : "Send anmeldelse"}
        </Button>
      </CardContent>
    </Card>
  );
}